import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ChevronRight, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLightbox } from "@/context/LightboxContext";
import { API, REPAIR_CATEGORIES } from "@/utils/constants";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

// Import refactored components
import { 
  ActiveRequestCard, 
  FulfilledRequestCard, 
  ArchivedRequestCard
} from "./RequestCards";
import { 
  StepProgress,
  SelectUnitStep,
  SelectCategoryStep,
  DescribeIssueStep,
  UploadImagesStep,
  CompleteRequestDialog
} from "./ServiceRequestForm";

export const ServiceRequestSection = ({ vehicles, autoOpenForm, onFormOpened, onUpdate, currentUser }) => {
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mileage, setMileage] = useState("");
  const [hours, setHours] = useState("");
  const [priority, setPriority] = useState("medium");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [repairRequests, setRepairRequests] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingRequest, setCompletingRequest] = useState(null);
  
  // Permission checks
  const userRole = currentUser?.role;
  const canCreate = hasPermission(userRole, PERMISSIONS.SERVICE_REQUEST.CREATE);
  const canMarkViewed = hasPermission(userRole, PERMISSIONS.SERVICE_REQUEST.MARK_VIEWED);
  const canMarkFulfilled = hasPermission(userRole, PERMISSIONS.SERVICE_REQUEST.MARK_FULFILLED);
  const canReopen = hasPermission(userRole, PERMISSIONS.SERVICE_REQUEST.REOPEN);
  const canArchive = hasPermission(userRole, PERMISSIONS.SERVICE_REQUEST.ARCHIVE);
  const canDelete = hasPermission(userRole, PERMISSIONS.SERVICE_REQUEST.DELETE);
  const [completeMileage, setCompleteMileage] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");
  const [completeImages, setCompleteImages] = useState([]);
  const [showFulfilled, setShowFulfilled] = useState(false);
  const { setLightboxImage } = useLightbox();

  useEffect(() => {
    if (autoOpenForm) {
      setShowForm(true);
      onFormOpened?.();
    }
  }, [autoOpenForm, onFormOpened]);

  const fetchRepairRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/repair-requests`);
      setRepairRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch repair requests", error);
    }
  }, []);

  useEffect(() => {
    fetchRepairRequests();
  }, [fetchRepairRequests]);

  const handleMileageChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || 0;
      setMileage(numValue > 0 ? numValue.toLocaleString() : '');
    }
  };

  const handleHoursChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || 0;
      setHours(numValue > 0 ? numValue.toLocaleString() : '');
    }
  };

  // Compress image to reduce payload size
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if larger than maxWidth
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to compressed JPEG
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        continue;
      }
      try {
        const compressed = await compressImage(file);
        setImages(prev => [...prev, compressed]);
      } catch (error) {
        console.error("Failed to compress image", error);
        toast.error("Failed to process image");
      }
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setStep(1);
    setSelectedVehicle(null);
    setMileage("");
    setHours("");
    setPriority("medium");
    setSelectedCategory("");
    setNotes("");
    setImages([]);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!selectedVehicle || !selectedCategory || !notes.trim()) {
      toast.error("Please complete all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Fleet Admin";
      const payload = {
        vehicle_id: selectedVehicle.id,
        unit_number: selectedVehicle.unit_number,
        mileage: mileage ? parseInt(mileage.replace(/,/g, '')) : null,
        hours: hours ? parseInt(hours.replace(/,/g, '')) : null,
        category: selectedCategory,
        priority: priority,
        notes: notes.trim(),
        images: images,
        requested_by: userName
      };

      await axios.post(`${API}/repair-requests`, payload);
      toast.success("Service request submitted successfully");
      resetForm();
      fetchRepairRequests();
    } catch (error) {
      console.error("Failed to submit service request", error);
      toast.error("Failed to submit service request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this repair request?")) return;
    try {
      await axios.delete(`${API}/repair-requests/${requestId}`);
      toast.success("Repair request deleted");
      fetchRepairRequests();
    } catch (error) {
      toast.error("Failed to delete repair request");
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    if (newStatus === 'fulfilled') {
      const request = repairRequests.find(r => r.id === requestId);
      setCompletingRequest(request);
      setCompleteMileage(request?.mileage ? request.mileage.toLocaleString() : "");
      setShowCompleteDialog(true);
      return;
    }
    
    setUpdatingStatus(requestId);
    try {
      const reviewerName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : null;
      let url = `${API}/repair-requests/${requestId}/status?status=${newStatus}`;
      if (newStatus === 'viewed' && reviewerName) {
        url += `&reviewed_by=${encodeURIComponent(reviewerName)}`;
      }
      await axios.put(url);
      const statusLabels = {
        submitted: "returned to pending",
        viewed: "marked as reviewed",
        fulfilled: "marked as fulfilled",
        archived: "archived"
      };
      toast.success(`Request ${statusLabels[newStatus] || newStatus}`);
      fetchRepairRequests();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleCompleteMileageChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value) {
      setCompleteMileage(parseInt(value).toLocaleString());
    } else {
      setCompleteMileage("");
    }
  };

  const handleConfirmComplete = async () => {
    if (!completingRequest) return;
    
    if (!completeNotes.trim()) {
      toast.error("Please add notes describing the work completed");
      return;
    }
    
    setUpdatingStatus(completingRequest.id);
    try {
      const mileageValue = completeMileage ? parseInt(completeMileage.replace(/,/g, '')) : null;
      const completedByName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Unknown';
      
      // Only send fields that need updating (avoid sending large image arrays)
      await axios.put(`${API}/repair-requests/${completingRequest.id}`, {
        mileage: mileageValue || completingRequest.mileage,
        completion_notes: completeNotes,
        completion_images: completeImages,
        completed_by: completedByName
      });
      
      await axios.put(`${API}/repair-requests/${completingRequest.id}/status?status=fulfilled`);
      toast.success("Service request completed successfully");
      fetchRepairRequests();
      if (onUpdate) onUpdate();
      setShowCompleteDialog(false);
      setCompletingRequest(null);
      setCompleteMileage("");
      setCompleteNotes("");
      setCompleteImages([]);
    } catch (error) {
      toast.error("Failed to complete request");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleCompleteImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        continue;
      }
      try {
        const compressed = await compressImage(file);
        setCompleteImages(prev => [...prev, compressed]);
      } catch (error) {
        console.error("Failed to compress image", error);
        toast.error("Failed to process image");
      }
    }
    e.target.value = '';
  };

  const removeCompleteImage = (index) => {
    setCompleteImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    if (vehicle) {
      if (vehicle.mileage > 0) setMileage(vehicle.mileage.toLocaleString());
      if (vehicle.engine_hours > 0) setHours(vehicle.engine_hours.toLocaleString());
    }
  };

  const activeRequests = repairRequests.filter(req => req.status === 'submitted' || req.status === 'viewed');
  const fulfilledRequests = repairRequests.filter(req => req.status === 'fulfilled');
  const archivedRequests = repairRequests.filter(req => req.status === 'archived');
  
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  const requestsThisWeek = repairRequests.filter(req => new Date(req.created_at) >= oneWeekAgo).length;
  const requestsThisMonth = repairRequests.filter(req => new Date(req.created_at) >= oneMonthAgo).length;
  const completedThisWeek = repairRequests.filter(req => 
    req.fulfilled_at && new Date(req.fulfilled_at) >= oneWeekAgo
  ).length;
  const highPriorityActive = activeRequests.filter(req => req.priority === 'high').length;

  const canProceed = () => {
    switch (step) {
      case 1: return selectedVehicle !== null;
      case 2: return selectedCategory !== "";
      case 3: return notes.trim() !== "";
      case 4: return true;
      default: return false;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Select Unit";
      case 2: return "Select Category";
      case 3: return "Describe Issue";
      case 4: return "Upload Images";
      default: return "";
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 sm:pt-6 lg:pt-8 max-w-full overflow-x-hidden">
      {/* Header with New Request Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900">Service Requests</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage and track service requests</p>
        </div>
        {canCreate && (
          <Button 
            onClick={() => setShowForm(true)} 
            className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
            data-testid="btn-new-request"
          >
            <Plus className="w-4 h-4 mr-2" /> New Service Request
          </Button>
        )}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-1">
            <span className="text-xl sm:text-2xl font-bold text-blue-600">{activeRequests.length}</span>
            {highPriorityActive > 0 && (
              <span className="text-[10px] sm:text-xs px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium whitespace-nowrap">
                {highPriorityActive} urgent
              </span>
            )}
          </div>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1">Active Requests</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="text-xl sm:text-2xl font-bold text-green-600">{completedThisWeek}</div>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1">Completed This Week</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="text-xl sm:text-2xl font-bold text-slate-700">{requestsThisWeek}</div>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1">Submitted This Week</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="text-xl sm:text-2xl font-bold text-slate-700">{requestsThisMonth}</div>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1">Submitted This Month</p>
        </div>
      </div>

      {/* New Service Request Modal */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); else setShowForm(true); }}>
        <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 p-4 border-b bg-white">
            <DialogTitle className="text-lg">New Service Request</DialogTitle>
          </DialogHeader>
          
          {/* Progress Steps - Compact */}
          <div className="px-4 py-3 flex-shrink-0 bg-slate-50 border-b">
            <StepProgress currentStep={step} />
            <h3 className="font-medium text-slate-900 text-center text-sm">{getStepTitle()}</h3>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="px-4 py-4">
              <div className="max-w-2xl mx-auto space-y-4">
                {step === 1 && (
                  <SelectUnitStep
                    vehicles={vehicles}
                    selectedVehicle={selectedVehicle}
                    onSelectVehicle={handleSelectVehicle}
                    mileage={mileage}
                    onMileageChange={handleMileageChange}
                    hours={hours}
                    onHoursChange={handleHoursChange}
                    priority={priority}
                    onPriorityChange={setPriority}
                  />
                )}

                {step === 2 && (
                  <SelectCategoryStep
                    selectedCategory={selectedCategory}
                    onSelectCategory={setSelectedCategory}
                  />
                )}

                {step === 3 && (
                  <DescribeIssueStep
                    notes={notes}
                    onNotesChange={setNotes}
                  />
                )}

                {step === 4 && (
                  <UploadImagesStep
                    images={images}
                    onImageUpload={handleImageUpload}
                    onRemoveImage={removeImage}
                    onImageClick={setLightboxImage}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <div className="flex gap-3 max-w-2xl mx-auto">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="flex-1"
                >
                  Back
                </Button>
              )}
              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="btn-next-step"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="btn-submit-request"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Requests Section */}
      <div className="bg-white rounded-xl border border-slate-200 mb-4">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <h3 className="font-semibold text-slate-900">Active Requests</h3>
            <span className="text-sm text-slate-500">({activeRequests.length})</span>
          </div>
        </div>
        
        {activeRequests.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <p className="text-slate-400">No active service requests</p>
            <Button 
              variant="outline" 
              className="mt-3"
              onClick={() => setShowForm(true)}
            >
              <Plus className="w-4 h-4 mr-2" /> Create First Request
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {activeRequests.map((request) => (
              <ActiveRequestCard
                key={request.id}
                request={request}
                updatingStatus={updatingStatus}
                onStatusUpdate={handleStatusUpdate}
                onDelete={handleDeleteRequest}
                onImageClick={setLightboxImage}
                canMarkViewed={canMarkViewed}
                canMarkFulfilled={canMarkFulfilled}
                canReopen={canReopen}
                canDelete={canDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Completed Requests Section (Collapsible) */}
      {fulfilledRequests.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 mb-4">
          <button
            onClick={() => setShowFulfilled(!showFulfilled)}
            className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="btn-toggle-fulfilled"
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <h3 className="font-semibold text-slate-900">Completed</h3>
              <span className="text-xs sm:text-sm text-slate-500">({fulfilledRequests.length})</span>
            </div>
            <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-400 transition-transform ${showFulfilled ? 'rotate-90' : ''}`} />
          </button>
          
          {showFulfilled && (
            <div className="border-t border-slate-200 divide-y divide-slate-200">
              {fulfilledRequests.map((request) => (
                <FulfilledRequestCard
                  key={request.id}
                  request={request}
                  updatingStatus={updatingStatus}
                  onStatusUpdate={handleStatusUpdate}
                  onDelete={handleDeleteRequest}
                  onImageClick={setLightboxImage}
                  canReopen={canReopen}
                  canArchive={canArchive}
                  canDelete={canDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Archived Requests Section (Hidden by default) */}
      {archivedRequests.length > 0 && (
        <div className="bg-slate-50 rounded-xl border border-slate-200">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="w-full p-4 flex items-center justify-between hover:bg-slate-100 transition-colors"
            data-testid="btn-toggle-archived"
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
              <h3 className="font-medium text-slate-600">Archived</h3>
              <span className="text-sm text-slate-400">({archivedRequests.length})</span>
            </div>
            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showArchived ? 'rotate-90' : ''}`} />
          </button>
          
          {showArchived && (
            <div className="border-t border-slate-200 divide-y divide-slate-200">
              {archivedRequests.map((request) => (
                <ArchivedRequestCard
                  key={request.id}
                  request={request}
                  onDelete={handleDeleteRequest}
                  canDelete={canDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Complete Service Request Dialog */}
      <CompleteRequestDialog
        isOpen={showCompleteDialog}
        onClose={() => {
          setShowCompleteDialog(false);
          setCompletingRequest(null);
          setCompleteMileage("");
          setCompleteNotes("");
          setCompleteImages([]);
        }}
        request={completingRequest}
        mileage={completeMileage}
        onMileageChange={handleCompleteMileageChange}
        notes={completeNotes}
        onNotesChange={setCompleteNotes}
        images={completeImages}
        onImageUpload={handleCompleteImageUpload}
        onRemoveImage={removeCompleteImage}
        onImageClick={setLightboxImage}
        onConfirm={handleConfirmComplete}
        isUpdating={updatingStatus === completingRequest?.id}
      />
    </div>
  );
};
